<template>
  <div class="story">
    <sheet>
      <loading v-if="isLoading"></loading>
      <template v-else>
        <h3 class="story-title">{{ articleData.title }}</h3>
        <div class="time-type">
          <p class="time-type__time">{{ articleData.formatPublished }}</p>
          <p class="time-type__type">{{ articleData.category?.name || '' }}</p>
        </div>
        <divide dashed mt="20" mb="55" />

        <img class="cover-img" :src="`${baseUrl}/${articleData.cover_path}`" />

        <div class="article-content" v-html="articleData.content"></div>

        <div class="article-attachment" v-if="attachment">
          <download-item :label="attachment.label" :link="attachment.link" />
        </div>

        <divide dashed mt="110" mb="30" />
        <more-announcement :title="`更多${articleData.category?.name || ''}`" />
      </template>
    </sheet>
  </div>
</template>

<script>
import { defineComponent, watch, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from 'dayjs';
import Divide from '@/components/Global/Divide.vue';
import MoreAnnouncement from '@/components/News/MoreAnnouncement.vue';
import { getIndexArticlesAPI } from '@/api/articles';
import articleMapping from '@/config/articleMapping';
import DownloadItem from '@/components/Global/DownloadItem.vue';
import useRequest from '@/hooks/useRequest';
import { useExtraBreadcrumbStore } from '@/store/extraBreadcrumb';
import useAsyncDialog from '@/hooks/useAsyncDialog';

export default defineComponent({
  name: 'Announcement',
  components: {
    Divide,
    MoreAnnouncement,
    DownloadItem,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const articleData = ref({});
    const attachment = ref(false);
    const extraBreadcrumbStore = useExtraBreadcrumbStore();
    const { asyncDialog } = useAsyncDialog();

    const { response, request, isLoading } = useRequest(getIndexArticlesAPI);
    request({
      type: 'news',
      uuid: route.query.id,
    });

    const baseUrl = process?.env?.VUE_APP_BASE_URL || window.location.host;

    const handleArticle = (res) => {
      const { result, status } = res.value;

      if (!status || result.data.length === 0) {
        asyncDialog({
          desc: '此內容已下架，請點擊確定返回首頁',
          callback: () => {
            router.push({ name: 'home' });
          },
        });
      }

      if (status) {
        extraBreadcrumbStore.setExtraBreadcrumb(result.title);
        const formatArticle = {
          ...result,
          formatPublished: dayjs(result.published).format('YYYY-MM-DD'),
        };

        articleData.value = formatArticle;

        if (result.file) {
          attachment.value = {
            label: result.file.name,
            link: `${baseUrl}/${result.file.path}`,
          };
        }
      }
    };

    watch(response, () => {
      handleArticle(response);
    });

    watch(
      () => route.query.id,
      () => {
        request({
          type: 'news',
          uuid: route.query.id,
        });
      },
    );

    return {
      articleData,
      articleMapping,
      isLoading,
      attachment,
      baseUrl,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@/styles/share/pureText.scss';
@import '@/styles/share/article.scss';

.story-title {
  @include font-style($c-main, 35, 500, 3.5px, 51px);
}

.time-type {
  @include flex();

  &__time {
    @include font-style($c-main, 16, 400, 1.6px, 28px);
  }

  &__type {
    position: relative;
    margin-left: 23px;
    @include font-style($c-assist-pink, 16, 400, 1.6px, 28px);

    &::before {
      content: '';
      width: 1px;
      height: 20px;
      @include position(tl, 50%, -12px);
      transform: translateY(-50%);
      display: inline-block;
      background-color: $c-main;
    }
  }
}

.img-box {
  &__img {
    width: 100%;
    max-width: 100%;
  }
}

.article-attachment {
  margin-top: 50px;
}

.cover-img {
  width: 100%;
  max-width: 600px;
  margin-bottom: 30px;
}

@media screen and (min-width: $tablet) {
  .img-box {
    &__img {
      max-width: 763px;
    }
  }
}
</style>
